import { useEffect, useRef, useState } from 'react';

// Distance (px) or downward speed (px/ms) past which a released drag closes.
const CLOSE_DISTANCE = 110;
const CLOSE_VELOCITY = 0.5;
// Upward flick speed that sends an expandable sheet straight to full height.
const EXPAND_VELOCITY = 0.5;
// How much of the viewport an expanded sheet may cover.
const MAX_HEIGHT_RATIO = 0.92;
// Movement before a touch is classified as a vertical drag vs a tap/scroll.
// 10px, not less: a tap on a button inside the sheet (an RPE rating, the
// weight +/−) often drifts a few pixels, and at 6px that drift became a drag
// whose click was then swallowed — the tap was lost.
const SLOP = 10;

const SETTLE = '0.28s cubic-bezier(0.32, 0.72, 0, 1)';

// While a sheet is on screen, the page underneath must not pull-to-refresh.
// The browser decides on the FIRST move of a touch whether a pull reloads the
// page, before the sheet knows it is a drag (see SLOP), so the sheet's
// preventDefault came too late: a swipe down to close it reloaded the app.
// `overscroll-behavior: none` on the root turns the pull off at the source.
// Counted, because a sheet can open from another.
let overscrollLocks = 0;
const lockPageOverscroll = () => {
  overscrollLocks += 1;
  if (overscrollLocks === 1) {
    document.documentElement.style.overscrollBehaviorY = 'none';
    document.body.style.overscrollBehaviorY = 'none';
  }
  return () => {
    overscrollLocks -= 1;
    if (overscrollLocks === 0) {
      document.documentElement.style.overscrollBehaviorY = '';
      document.body.style.overscrollBehaviorY = '';
    }
  };
};

const isFormField = (node) =>
  node instanceof Element &&
  !!node.closest('input, textarea, select, [contenteditable="true"], [data-sheet-no-drag]');

/**
 * Nearest vertically scrollable ancestor of `node`, up to and INCLUDING the
 * sheet: some sheets are their own scroll container (the video upload sheet is
 * `max-h-[92dvh] overflow-y-auto`), and a drag down in them must scroll back up
 * before it may move the sheet.
 */
export const findScroller = (node, sheet) => {
  let el = node instanceof Element ? node : null;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
      return el;
    }
    if (el === sheet) break;
    el = el.parentElement;
  }
  return null;
};

/**
 * Swipe gestures for a bottom sheet: drag it down to close and, when
 * `expandable`, drag it up to make it taller. It follows the finger, then
 * settles at one of two sizes on release: its natural height or 92% of the
 * screen, whichever is closer (a flick picks the direction).
 *
 * Returns a callback ref for the sheet element. The hook writes only the CSS
 * `translate` and `height` properties, never `transform`, so it composes with
 * whatever animates the sheet in and out (framer-motion's `y`, Radix +
 * tailwindcss-animate keyframes): a sheet released past the threshold simply
 * continues its exit animation from where the finger left it.
 *
 * A touch that starts inside a scrolled list scrolls the list; the sheet only
 * takes over once that list is back at its top (and, when expanding, once the
 * sheet has reached full height the list scrolls again). Form fields and
 * anything marked `data-sheet-no-drag` never start a drag.
 *
 * While the sheet is taller than its natural height it carries a
 * `data-sheet-resized` attribute, so a capped inner list can drop its cap:
 * `max-h-[60dvh] [[data-sheet-resized]_&]:max-h-none`.
 */
export default function useSheetDrag({ onClose, expandable = false, enabled = true }) {
  const [sheet, setSheet] = useState(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!sheet || !enabled) return undefined;

    const unlockOverscroll = lockPageOverscroll();
    let gesture = null;
    // Height the sheet has with no inline height — measured the first time it
    // is resized, so shrinking back stops there before the sheet slides down.
    let naturalHeight = null;

    const maxHeight = () => Math.round(window.innerHeight * MAX_HEIGHT_RATIO);

    const setTranslate = (y, animate) => {
      sheet.style.transition = animate ? `translate ${SETTLE}, height ${SETTLE}` : 'none';
      sheet.style.translate = y ? `0 ${y}px` : '';
    };

    const setHeight = (h, animate) => {
      sheet.style.transition = animate ? `translate ${SETTLE}, height ${SETTLE}` : 'none';
      if (h == null) {
        sheet.style.height = '';
        sheet.style.maxHeight = '';
        sheet.removeAttribute('data-sheet-resized');
      } else {
        sheet.style.height = `${h}px`;
        sheet.style.maxHeight = `${maxHeight()}px`;
        sheet.setAttribute('data-sheet-resized', '');
      }
    };

    const start = (target, x, y) => {
      if (isFormField(target)) return;
      gesture = {
        target,
        startX: x,
        startY: y,
        active: false,
        scroller: findScroller(target, sheet),
        samples: [{ y, t: performance.now() }],
      };
    };

    // Returns true when the sheet owns this move (caller then blocks scrolling).
    const move = (x, y) => {
      if (!gesture) return false;
      const dx = x - gesture.startX;
      const dy = y - gesture.startY;

      if (!gesture.active) {
        if (Math.abs(dy) < SLOP && Math.abs(dx) < SLOP) return false;
        if (Math.abs(dx) > Math.abs(dy)) {
          gesture = null; // horizontal: leave it to carousels & co.
          return false;
        }
        const { scroller } = gesture;
        const currentHeight = sheet.getBoundingClientRect().height;
        const canGrow = expandable && currentHeight < maxHeight() - 1;
        const takes = dy > 0 ? !scroller || scroller.scrollTop <= 0 : canGrow;
        if (!takes) {
          gesture = null;
          return false;
        }
        if (naturalHeight == null && !sheet.hasAttribute('data-sheet-resized')) {
          naturalHeight = currentHeight;
        }
        gesture.active = true;
        gesture.originY = y;
        gesture.startHeight = currentHeight;
      }

      const offset = y - gesture.originY;
      gesture.samples.push({ y, t: performance.now() });
      if (gesture.samples.length > 5) gesture.samples.shift();

      if (expandable) {
        const wanted = gesture.startHeight - offset;
        const floor = naturalHeight ?? gesture.startHeight;
        if (wanted > floor) {
          setHeight(Math.min(wanted, maxHeight()), false);
          setTranslate(0, false);
        } else {
          if (sheet.hasAttribute('data-sheet-resized')) setHeight(floor, false);
          setTranslate(floor - wanted, false);
        }
      } else {
        // Upward drag on a fixed-size sheet only gives a little rubber band.
        setTranslate(offset > 0 ? offset : offset / 6, false);
      }
      return true;
    };

    const end = () => {
      if (!gesture) return;
      const { active, samples } = gesture;
      gesture = null;
      if (!active) return;

      const first = samples[0];
      const last = samples[samples.length - 1];
      const velocity = last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0;
      const translated = parseFloat((sheet.style.translate || '0 0').split(' ')[1]) || 0;

      if (translated > CLOSE_DISTANCE || (velocity > CLOSE_VELOCITY && translated > 10)) {
        // Keep the offset: the exit animation carries on from here.
        sheet.style.transition = 'none';
        onCloseRef.current?.();
        return;
      }

      setTranslate(0, true);
      if (!expandable || !sheet.hasAttribute('data-sheet-resized')) return;
      // Two resting sizes only — natural and full. Left wherever the finger
      // let go, the sheet ended at random heights with a row cut in half, and
      // a later swipe down shrank it by a few pixels instead of closing it.
      // Past halfway (or flicked up) it goes full; otherwise back to natural.
      const height = sheet.getBoundingClientRect().height;
      const full = maxHeight();
      const natural = naturalHeight ?? height;
      const goFull = velocity < -EXPAND_VELOCITY
        || (velocity <= CLOSE_VELOCITY && height > (natural + full) / 2);
      if (goFull) {
        setHeight(full, true);
      } else if (naturalHeight != null) {
        // Back to (almost) natural size: drop the inline height entirely so
        // the sheet follows its content again.
        setHeight(naturalHeight, true);
        window.setTimeout(() => {
          // Compared as numbers: the browser rounds the inline value
          // ("596.188px" for a measured 596.1875), so the strings never match.
          if (!gesture && Math.abs(parseFloat(sheet.style.height) - naturalHeight) < 1) setHeight(null, false);
        }, 300);
      }
    };

    // Touch: listeners are non-passive so an owned move can preventDefault
    // the native scroll / pull-to-refresh underneath.
    const onTouchStart = (e) => {
      if (e.touches.length !== 1) {
        gesture = null;
        return;
      }
      start(e.target, e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchMove = (e) => {
      if (e.touches.length !== 1) return;
      if (move(e.touches[0].clientX, e.touches[0].clientY) && e.cancelable) e.preventDefault();
    };

    // Mouse (desktop / devtools): same gesture, tracked on window once started.
    const onMouseMove = (e) => {
      if (move(e.clientX, e.clientY)) e.preventDefault();
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      end();
    };
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      start(e.target, e.clientX, e.clientY);
      if (!gesture) return;
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };

    // A drag that moved must not also fire the click of the row it started on.
    let suppressClick = false;
    const onMoveFlag = () => {
      if (gesture?.active) suppressClick = true;
    };
    const onClickCapture = (e) => {
      if (suppressClick) {
        e.preventDefault();
        e.stopPropagation();
      }
      suppressClick = false;
    };
    const onDown = () => {
      suppressClick = false;
    };

    sheet.addEventListener('touchstart', onTouchStart, { passive: true });
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    sheet.addEventListener('touchmove', onMoveFlag, { passive: true });
    sheet.addEventListener('touchend', end);
    sheet.addEventListener('touchcancel', end);
    sheet.addEventListener('mousedown', onDown, true);
    sheet.addEventListener('touchstart', onDown, { capture: true, passive: true });
    sheet.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMoveFlag);
    sheet.addEventListener('click', onClickCapture, true);

    return () => {
      unlockOverscroll();
      sheet.removeEventListener('touchstart', onTouchStart);
      sheet.removeEventListener('touchmove', onTouchMove);
      sheet.removeEventListener('touchmove', onMoveFlag);
      sheet.removeEventListener('touchend', end);
      sheet.removeEventListener('touchcancel', end);
      sheet.removeEventListener('mousedown', onDown, true);
      sheet.removeEventListener('touchstart', onDown, { capture: true });
      sheet.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMoveFlag);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      sheet.removeEventListener('click', onClickCapture, true);
    };
  }, [sheet, enabled, expandable]);

  return setSheet;
}
